import React, { useState, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { Button, Typography } from 'antd'
import { FormattedMessage } from 'react-intl'

import { FlexRow } from '../Components/FlexboxReusables/FlexItems'
import { ThemeContext } from '../Theme/ThemeContext'
import { UserContext } from '../Components/User/UserContext'
import ClientProfileSummarySegment from '../Components/Client/ClientProfileSummarySegment'
import { AuthContext } from '../Components/Auth/AuthContext'
import { SaveOutlined } from '@ant-design/icons'
import CompanySegment from '../Components/Client/CompanySegment'

const notAvailable = '/'

/*
  This file and its children are largely the same as UserSettings.tsx (and children), but it's 
  better to have two separate, distinct versions than to have only one with countless conditionals
  that are hard to read and maintain. Thats why it uses the core functionality (like context) from
  UserContext.tsx while still referring to the 'Client' components.
  Just for Context: 'Clients' are usually Companies.
*/

const ClientSettings: React.FC = () => {
  const { isDarkMode } = useContext(ThemeContext)
  const { authUser } = useContext(AuthContext)

  const { userDetails, changeUserDetails } = useContext(UserContext)

  const [ email, setEmail ] = useState<string>(userDetails.email)
  const [ phone, setPhone ] = useState<string>(userDetails.phone || notAvailable)

  const [ country, setCountry ] = useState<string>(userDetails.country || notAvailable)
  const [ street, setStreet ] = useState<string>(userDetails.street || notAvailable)
  const [ zipCode, setZipCode ] = useState<string>(userDetails.zipCode || notAvailable)

  const [ companyName, setCompanyName ] = useState<string>(userDetails.company?.name || notAvailable)
  const [ companyStreet, setCompanyStreet ] = useState<string>(userDetails.company?.street || notAvailable)
  const [ companyZipCode, setCompanyZipCode ] = useState<string>(userDetails.company?.zipCode || notAvailable)
  const [ companyCity, setCompanyCity ] = useState<string>(userDetails.company?.city || notAvailable)
  const [ companyCountry, setCompanyCountry ] = useState<string>(userDetails.company?.country || notAvailable)

  const [ editProfilePicture, setEditProfilePicture ] = useState<boolean>(false)
  const [ editBuyersDetails, setEditBuyersDetails ] = useState<boolean>(false)

  useEffect(() => {
    setEmail(userDetails.email)
    setPhone(userDetails.phone || notAvailable)

    setCountry(userDetails.country || notAvailable)
    setStreet(userDetails.street || notAvailable)
    setZipCode(userDetails.zipCode || notAvailable)

    setCompanyName(userDetails.company?.name || notAvailable)
    setCompanyStreet(userDetails.company?.street || notAvailable)
    setCompanyZipCode(userDetails.company?.zipCode || notAvailable)
    setCompanyCity(userDetails.company?.city || notAvailable)
    setCompanyCountry(userDetails.company?.country || notAvailable)
  }, [ userDetails ])

  const onChangeUserDetails = () => {
    changeUserDetails(
      {
        id: authUser.id,
        email,
        phone,
        country,
        street,
        zipCode,
        company: userDetails.company ? {
          id: userDetails.company.id,
          name: companyName,
          street: companyStreet,
          zipCode: companyZipCode,
          city: companyCity,
          country: companyCountry
        } : undefined
      }
    )
  }

  return (
    <>
      <FlexRow width='100%' justifyContent='center' height='5em'>
        <Typography.Title level={2}>
          <FormattedMessage id='clientSettings.title' />
        </Typography.Title>
      </FlexRow>
      <Container isDarkMode={isDarkMode}>
        <FlexRow height='2em' margin='0 0 2em 0' width='95%' justifyContent='flex-end'>
          <Button type='primary' onClick={onChangeUserDetails}>
            <SaveOutlined style={{marginRight: '1em'}}/>
            <FormattedMessage id='generic.save' />
          </Button>
        </FlexRow>
        <SegmentContainer>
          <ClientProfileSummarySegment
            id={authUser.id}
            companyName={companyName}
            setEditProfilePicture={setEditProfilePicture}
            editProfilePicture={editProfilePicture}
          />
        </SegmentContainer>

        <SegmentContainer>
          <CompanySegment
            id={userDetails.company?.id || 0}
            name={companyName}
            setCompanyName={setCompanyName}
            email={email}
            setEmail={setEmail}
            street={companyStreet}
            setCompanyStreet={setCompanyStreet}
            zipCode={companyZipCode}
            setCompanyZipCode={setCompanyZipCode}
            city={companyCity}
            setCompanyCity={setCompanyCity}
            country={companyCountry}
            setCompanyCountry={setCompanyCountry}
            setEditBuyersDetails={setEditBuyersDetails}
            editBuyersDetails={editBuyersDetails}
          />
        </SegmentContainer>
      </Container>
    </>
  )
}

export default ClientSettings

const Container = styled.div<{isDarkMode: boolean}>`
  width: 95%;
  height: 100%;
  margin: 0 auto;
  padding-top: 2em;
  padding-bottom: 2em;
  border-radius: 2em;
  background-color: ${({ isDarkMode }) => isDarkMode ? '#1e1e1e' : 'white'};
`

const SegmentContainer = styled.div`
  width: 90%;
  margin: 0 auto 4em auto;
  padding: 1.5em;
  border-radius: 2em;
  border: 0.1em dashed darkgrey;
`